import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { Message } from '../../contracts/message.interface';
import { MessageBubbleComponent } from '../message/message-bubble/message-bubble.component';
import { Dialog } from 'primeng/dialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import {Subject, take, takeUntil} from 'rxjs';
import { ChatHistoryService } from '../../services/chat-history.service';
import { ChatMessagesPage, MessageCursor } from '../../contracts/chat-message-page.interface';

@Component({
  selector: 'app-chat-history-drawer',
  standalone: true,
  imports: [NgForOf, NgIf, Dialog, MessageBubbleComponent, ProgressSpinner],
  templateUrl: './chat-history-drawer.component.html',
  styleUrls: ['./chat-history-drawer.component.scss']
})
export class ChatHistoryDrawerComponent implements OnChanges, OnDestroy {

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() anchorMessageId?: number;
  @Input({ required: true }) conversationId!: number;

  @ViewChild('scrollContainer', { static: true })
  scrollEl!: ElementRef<HTMLDivElement>;

  messages: Message[] = [];
  loading = false;
  hasMoreBefore = true;
  hasMoreAfter = false;
  cursorBefore?: MessageCursor;
  cursorAfter?: MessageCursor;

  private destroyed$ = new Subject<void>();
  private firstLoadDone = false; // Evita scroll forzato multiplo

  constructor(private chatService: ChatHistoryService, private zone: NgZone) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue) {
      this.resetState();
      if (this.anchorMessageId) {
        this.initContext();
      } else {
        this.initLatest();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private resetState(): void {
    this.messages = [];
    this.cursorBefore = undefined;
    this.hasMoreBefore = true;
    this.loading = false;
    this.firstLoadDone = false;
  }

  /** Caricamento iniziale LATEST */
  private initLatest(): void {
    this.loading = true;
    this.anchorMessageId = undefined;
    this.cursorAfter = undefined;
    this.hasMoreAfter = false;
    this.chatService.getLatest(this.conversationId, 50)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((page: ChatMessagesPage) => {
        this.messages = page.messages;
        this.cursorBefore = page.cursorBefore;
        this.hasMoreBefore = !!page.cursorBefore;

        // Scroll al fondo SOLO se è il primo caricamento (lista vuota prima)
        if (this.messages.length === page.messages.length) {
          this.scrollToBottom();
        }

        this.loading = false;
      });
  }

  private initContext(): void {
    this.loading = true;
    this.chatService.getContext(this.conversationId, 50, undefined, undefined, this.anchorMessageId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((page: ChatMessagesPage) => {
        this.messages = page.messages;
        this.cursorBefore = page.cursorBefore;
        this.cursorAfter = page.cursorAfter;
        this.hasMoreBefore = !!page.cursorBefore;
        this.hasMoreAfter = !!page.cursorAfter;

        // Scroll al fondo SOLO se è il primo caricamento (lista vuota prima)
        // if (this.messages.length === page.messages.length) {
        //   this.scrollToBottom();
        // }
        this.scrollToAnchor();
        this.loading = false;
      });
  }

  /** Scroll handler */
  onScroll(): void {
    const el = this.scrollEl.nativeElement;

    // Scroll up: top < 120px
    if (el.scrollTop < 120 && this.hasMoreBefore && !this.loading) {
      this.loadBefore();
    }

    // Scroll down: bottom < 120px
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120 && this.hasMoreAfter && !this.loading) {
      this.loadAfter();
    }
  }

  /** Caricamento messaggi precedenti */
  private loadBefore(): void {
    if (!this.cursorBefore) return;
    this.loading = true;

    const container = this.scrollEl.nativeElement;
    // const prevScrollHeight = container.scrollHeight;

    let scrollRequest = this.chatService.getLatest(this.conversationId, 50, this.cursorBefore);
    if (this.anchorMessageId) {
      scrollRequest = this.chatService.getContext(this.conversationId, 50, this.cursorBefore);
    }

    scrollRequest
      .pipe(takeUntil(this.destroyed$))
      .subscribe((page: ChatMessagesPage) => {
        this.messages = [...page.messages, ...this.messages];
        this.cursorBefore = page.cursorBefore;
        this.hasMoreBefore = !!page.cursorBefore;

        // attendi che Angular finisca il rendering dei nuovi messaggi
        // this.zone.onStable.pipe(take(1)).subscribe(() => {
        //   const newScrollHeight = container.scrollHeight;
        //   container.scrollTop += newScrollHeight - prevScrollHeight;
        // });
        this.loading = false;
      });
  }

  /** Caricamento messaggi successivi (scroll down) */
  private loadAfter(): void {
    if (!this.cursorAfter) return;
    this.loading = true;

    const container = this.scrollEl.nativeElement;
    // const prevScrollTop = container.scrollTop;

    let scrollRequest = this.chatService.getContext(
      this.conversationId,
      50,
      undefined,
      this.cursorAfter,
      undefined
    );

    scrollRequest
      .pipe(takeUntil(this.destroyed$))
      .subscribe((page: ChatMessagesPage) => {
        this.messages = [...this.messages, ...page.messages];
        this.cursorAfter = page.cursorAfter;
        this.hasMoreAfter = !!page.cursorAfter;

        // attendi che Angular finisca il rendering dei nuovi messaggi
        // this.zone.onStable.pipe(take(1)).subscribe(() => {
        //   // mantieni la scroll position corrente per non saltare
        //   container.scrollTop = prevScrollTop;
        // });
        this.loading = false;
      });
  }

  /** Scroll al fondo (solo al primo caricamento) */
  private scrollToBottom(): void {
    if (!this.scrollEl) return;
    const el = this.scrollEl.nativeElement;

    this.zone.onStable.pipe(takeUntil(this.destroyed$), take(1)).subscribe(() => {
      // Use a timeout to wait for media to render and correctly adjust scroll height
      setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 100);
    });
  }

  private scrollToAnchor(): void {
    const container = this.scrollEl.nativeElement;

    this.zone.onStable.pipe(take(1)).subscribe(() => {
      setTimeout(() => {
        const anchorEl = container.querySelector('[data-anchor="true"]') as HTMLElement;
        if (!anchorEl) return;

        const anchorRect = anchorEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop
          + anchorRect.top - containerRect.top
          - container.clientHeight / 2
          + anchorRect.height / 2;

        container.scrollTop = Math.max(scrollTop, 0);
      }, 100);
    });
  }

  /** Chiusura drawer */
  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
